import { Text, type TextProps } from "react-native";

import { cn } from "@/lib/ui/cn";

/**
 * The size ramps a money figure steps down through; a longer string takes a smaller step.
 *
 * All stay inside the documented type scale (DESIGN §3): `metric` is the KPI-tile step (17px
 * Title-sized numeral down to the 13px numeral sublabel), and `scoreboard` the larger show-mode
 * figure (20px down to 15px). Nothing drops below 13px; a figure that would need to is a layout
 * problem, not a typography one.
 */
const RAMPS = {
  /** A tile sharing its row with another, about half a phone's width. */
  metric: { steps: ["text-[17px]", "text-[15px]", "text-[13px]"], breaks: [9, 12] },
  /** A tile that owns the whole row, so it holds its size much longer. */
  metricWide: { steps: ["text-[17px]", "text-[15px]", "text-[13px]"], breaks: [14, 18] },
  /** The larger show-mode figure, two per row. */
  scoreboard: { steps: ["text-xl", "text-[17px]", "text-[15px]"], breaks: [9, 12] },
  /**
   * The Display step (DESIGN §3, 24px) for a figure that must outrank a `scoreboard` row in the
   * same card: the show Drawer, which sits above the Activity strip and must read first because a
   * vendor checks it between deals. At the same 20px as the stats beside it, whichever figure was
   * the longer string won the glance. It holds its size longer than `scoreboard` because it owns
   * the row (minus a chevron), not half of one.
   */
  display: { steps: ["text-2xl", "text-xl", "text-[17px]"], breaks: [11, 14] },
  /**
   * A summary strip's anchor figure: the loudest number on a list screen, sharing that screen with
   * rows of `metric` figures rather than owning a hero surface.
   *
   * `display` (24px) reads as a hero block above a dense list, which §6 rules out. `scoreboard`
   * paints the wanted 20px but has the show-mode figure's tight break points: at "$23,111.78" (10
   * characters) it drops to 17px and ties the per-card Ask it should outrank. So this pairs the 20px
   * top step with `display`'s wider breaks, for a figure with most of a row to itself.
   */
  summary: { steps: ["text-xl", "text-[17px]", "text-[15px]"], breaks: [11, 14] },
  /** The Book landing headline, owning a full phone row. */
  headline: {
    steps: [
      "text-[40px] leading-[46px]",
      "text-[34px] leading-[40px]",
      "text-[28px] leading-[34px]",
    ],
    breaks: [10, 13],
  },
} as const;

/**
 * A money figure that never misleads by truncation.
 *
 * `numberOfLines={1}` alone turns "$15,521.04" into "$15,5…", which reads as a complete number, so a
 * dealer cannot tell $15,500 from $15.50. Dropping the clamp is worse: the figure wraps mid-number
 * ("$1,076.1" / "2"), a wrong number that looks whole. So this steps the type down one documented
 * size at a time as the string grows, and only then clamps:
 *
 * - The step is chosen from the string length against the ramp's break points, so it is
 *   deterministic and identical on native and web (react-native-web ignores
 *   `adjustsFontSizeToFit`). The break points are per ramp because they encode how much room the
 *   call site has.
 * - `adjustsFontSizeToFit` adds a continuous safety net under the ramp on native. The async Book
 *   headline opts out, because iOS can keep the loading dash's tiny fitted measurement when the
 *   real value arrives, leaving a 40px figure unreadable.
 * - `maxFontSizeMultiplier` bounds OS large-text scaling, which would otherwise re-create the
 *   overflow.
 *
 * Callers pass the formatted string (usually from `signedMoney`) and its spoken form, so the a11y
 * label carries the exact value even at a small visual step.
 */
export function MoneyFigure({
  value,
  a11y,
  size = "metric",
  className,
  testID,
  accessibilityRole,
}: {
  value: string;
  /** Spoken form, for example "up $15,521.04". Falls back to the visible string. */
  a11y?: string;
  /** The ramp the figure steps down through, chosen by how much room the call site has. */
  size?: keyof typeof RAMPS;
  className?: string;
  testID?: string;
  accessibilityRole?: TextProps["accessibilityRole"];
}) {
  const { steps, breaks } = RAMPS[size];
  const step = value.length <= breaks[0]
    ? steps[0]
    : value.length <= breaks[1]
    ? steps[1]
    : steps[2];
  return (
    <Text
      testID={testID}
      className={cn("font-num-bold text-foreground", step, className)}
      numberOfLines={1}
      adjustsFontSizeToFit={size !== "headline"}
      minimumFontScale={0.8}
      maxFontSizeMultiplier={1.3}
      accessibilityLabel={a11y ?? value}
      accessibilityRole={accessibilityRole}
    >
      {value}
    </Text>
  );
}
